// The name the login form opens with, so signing back in is one tap and a
// password instead of "what did I call myself?". Written once a sign-in has
// actually succeeded (useAuth), which is the only moment we know the identifier
// is real, and deliberately NOT cleared on logout — surviving logout is the
// entire point.
//
// A cookie rather than localStorage so the value is scoped and expired by the
// browser rather than by us, and so it is one mechanism with the rest of auth
// rather than a second one. Nothing reads it server-side: the login form reads
// it from `document.cookie` on mount, which is why it is NOT httpOnly — that is
// the one flag this value must not have. It carries no `domain`, so it stays
// host-only and does not follow the account onto api. or a per-space domain;
// that is narrower than the auth cookies on purpose. The mobile implementation
// (lastUsername.native.ts) uses AsyncStorage; there are no cookies there.
//
// This is not "remember me": no credential and no session is stored, so nothing
// here can sign anyone in. But be honest about what it CAN hold — the login form
// stores the identifier as typed (apps/factiii-web/src/components/auth/Login.tsx), and
// sign-in accepts an email address, so for anyone who signs in that way this is
// their email address, in a cookie any script on the origin can read, for a year.
// That is a real disclosure and the reason `Secure` is not optional below. The
// "remember me" checkbox people expect is about keeping a SESSION alive, which
// our JWT cookie already does on its own terms — a toggle over an identifier
// would promise something this does not do.
const COOKIE = 'ftLastUsername';
const ONE_YEAR_S = 60 * 60 * 24 * 365;

// Not a constant: dev serves the app over http on a LAN address, where a Secure
// cookie is dropped on the floor without an error and the field silently stops
// remembering anything. Every deployed origin is https, so every deployed write
// gets the flag.
const secureFlag = (): string =>
  typeof location !== 'undefined' && location.protocol === 'https:'
    ? '; Secure'
    : '';

// Usernames are [A-Za-z0-9_-]-ish and emails contain @ and . — all cookie-safe,
// but encode anyway so a value we did not anticipate can never break the header
// or smuggle in a second attribute.
export async function rememberUsername(username: string): Promise<void> {
  if (typeof document === 'undefined' || !username) return;
  document.cookie = `${COOKIE}=${encodeURIComponent(username)}; path=/; max-age=${ONE_YEAR_S}; SameSite=Lax${secureFlag()}`;
}

export async function readRememberedUsername(): Promise<string | null> {
  if (typeof document === 'undefined') return null;
  const match = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${COOKIE}=`));
  if (!match) return null;
  try {
    return decodeURIComponent(match.slice(COOKIE.length + 1)) || null;
  } catch {
    // A half-written or hand-edited cookie is not worth an exception inside a
    // login form; opening empty is the correct fallback.
    return null;
  }
}

export async function forgetUsername(): Promise<void> {
  if (typeof document === 'undefined') return;
  // Name, domain and path are what identify the cookie to overwrite, so the
  // flags are not load-bearing here — they match the write so the two lines
  // never have to be read against each other.
  document.cookie = `${COOKIE}=; path=/; max-age=0; SameSite=Lax${secureFlag()}`;
}
